import {Button} from '@wandb/weave/components/Button';
import React, {useCallback} from 'react';

import {type Ref as PanelBankSectionConfigRef} from '../../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../../util/panelbankFlow';
import {StyledButtonWrapper} from '../PanelActions.style';
import {usePanelContext} from '../PanelContextProvider';
import {PanelPopupMode} from '../types';
import {FullScreenPanelLink} from './FullScreenPanelLink';

export const PanelFullScreenButton = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigRef | undefined;
}) => {
  return (
    <WorkspaceFullScreenPanelButton
      panelBankSectionConfigRef={panelBankSectionConfigRef}
    />
  );
};

const FullScreenButton = ({onClick}: {onClick: () => void}) => {
  return (
    <StyledButtonWrapper title="View fullscreen">
      <Button
        className="text-moon-600"
        icon="full-screen-mode-expand"
        variant="ghost"
        aria-label="View full screen"
        data-dd-action-name="View full screen"
        size="small"
        tooltip={'View full screen'}
        tooltipProps={{className: 'py-8 px-12'}}
        onClick={onClick}
      />
    </StyledButtonWrapper>
  );
};

/**
 * Clicking the full screen button in a workspace should redirect to the full
 * screen panel page where it's overlayed on top of panel bank.
 */
const WorkspaceFullScreenPanelButton = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigRef | undefined;
}) => {
  const {panel, trackPanelControls} = usePanelContext();
  const onClick = useCallback(() => {
    window.scrollTo(0, 0);

    trackPanelControls({
      action: 'View full screen clicked',
    });
  }, [trackPanelControls]);

  return (
    <FullScreenPanelLink
      panel={panel}
      panelBankSectionConfigRef={panelBankSectionConfigRef}>
      <FullScreenButton onClick={onClick} />
    </FullScreenPanelLink>
  );
};

/**
 * Report's full screen should still render the popup modal.
 * It has not been migrated to the same flow as in workspaces.
 */
export const ReportFullScreenPanelButton = () => {
  const {trackPanelControls} = usePanelContext();
  const {setPanelPopupMode} = usePanelContext();

  const onClick = useCallback(() => {
    setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    trackPanelControls({
      action: 'View full screen clicked',
    });
  }, [setPanelPopupMode, trackPanelControls]);

  if (isMobile()) {
    return null;
  }

  return <FullScreenButton onClick={onClick} />;
};
